import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ContractorPortalUser } from '@/hooks/useContractorPortalUsers';
import { Mail, Edit, Power, Trash2, Clock } from 'lucide-react';
import { format } from 'date-fns';

interface PortalUsersListProps {
  users: ContractorPortalUser[];
  onEdit: (user: ContractorPortalUser) => void;
  onResendInvitation: (userId: string) => void;
  onToggleActive: (userId: string, isActive: boolean) => void;
  onDelete: (userId: string) => void;
}

export function PortalUsersList({ 
  users, 
  onEdit, 
  onResendInvitation, 
  onToggleActive, 
  onDelete 
}: PortalUsersListProps) {
  if (users.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No portal users found. Create one to get started.
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Last Login</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell>
                <div>
                  <div className="font-medium">
                    {user.first_name || user.last_name 
                      ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                      : 'No name'}
                  </div>
                  {user.title && (
                    <div className="text-sm text-muted-foreground">{user.title}</div>
                  )}
                </div>
              </TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {user.is_primary_contact && (
                    <Badge variant="secondary" className="text-xs">Primary</Badge>
                  )}
                  {user.can_upload_documents && (
                    <Badge variant="outline" className="text-xs">Upload</Badge>
                  )}
                  {user.can_view_prequalification_status && (
                    <Badge variant="outline" className="text-xs">View Status</Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {user.is_active ? (
                    <Badge variant="default" className="bg-success text-success-foreground">Active</Badge>
                  ) : (
                    <Badge variant="secondary">Inactive</Badge>
                  )}
                  {!user.auth_user_id && user.invitation_sent_at && (
                    <Badge variant="outline" className="text-xs">
                      <Clock className="h-3 w-3 mr-1" />
                      Pending
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                {user.last_login_at ? (
                  <span className="text-sm">
                    {format(new Date(user.last_login_at), 'MMM d, yyyy')}
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">Never</span>
                )}
              </TableCell>
              <TableCell>
                <div className="flex justify-end gap-1">
                  {!user.auth_user_id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onResendInvitation(user.id)}
                      title="Resend Invitation"
                    >
                      <Mail className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(user)}
                    title="Edit User"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onToggleActive(user.id, !user.is_active)}
                    title={user.is_active ? 'Deactivate' : 'Activate'}
                  >
                    <Power className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(user.id)}
                    title="Delete User"
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
